import * as React from 'react';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebounce } from '@/hooks/use-debounce';

export interface PaginatedResponse<T> {
  rows: T[];
  totalCount?: number;
  hasNextPage?: boolean;
}

export interface PaginatedComboboxProps<T> {
  /** Query key for react-query caching */
  queryKey: string[];

  /** Fetch function that returns paginated data */
  fetchFn: (params: {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string;
  }) => Promise<PaginatedResponse<T>>;

  /** Get the unique ID from an item */
  getItemId: (item: T) => string;

  /** Get the display label from an item */
  getItemLabel: (item: T) => string;

  /** Currently selected item ID */
  value?: string;

  /** Callback when selection changes */
  onSelect: (item: T | null) => void;

  /** Placeholder text for the trigger button */
  placeholder?: string;

  /** Placeholder text for the search input */
  searchPlaceholder?: string;

  /** Optional grouping function - returns group key for an item */
  getItemGroup?: (item: T) => string;

  /** Optional group label formatter */
  formatGroupLabel?: (groupKey: string) => string;

  /** Disabled state */
  disabled?: boolean;

  /** Button width */
  className?: string;

  /** Page size for fetching */
  pageSize?: number;

  /** Search debounce delay in ms */
  searchDebounce?: number;
}

export function PaginatedCombobox<T>({
  queryKey,
  fetchFn,
  getItemId,
  getItemLabel,
  value,
  onSelect,
  placeholder = 'Select item...',
  searchPlaceholder = 'Search...',
  getItemGroup,
  formatGroupLabel = (key) => key,
  disabled = false,
  className = 'w-[300px]',
  pageSize = 20,
  searchDebounce = 300,
}: PaginatedComboboxProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearch = useDebounce(searchTerm, searchDebounce);
  const listRef = React.useRef<HTMLDivElement>(null);

  // Infinite query for paginated data
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [...queryKey, debouncedSearch],
    queryFn: ({ pageParam = 1 }) =>
      fetchFn({
        pageNumber: pageParam,
        pageSize,
        searchTerm: debouncedSearch || undefined,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasNextPage === false) return undefined;
      if (lastPage.rows.length < pageSize) return undefined;
      return allPages.length + 1;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array
  const allItems = React.useMemo(() => {
    return data?.pages.flatMap((page) => page.rows) ?? [];
  }, [data]);

  // Find selected item
  const selectedItem = React.useMemo(() => {
    return allItems.find((item) => getItemId(item) === value);
  }, [allItems, value, getItemId]);

  // Group items if grouping function is provided
  const groupedItems = React.useMemo(() => {
    if (!getItemGroup) {
      return { ungrouped: allItems };
    }

    const groups: Record<string, T[]> = {};
    for (const item of allItems) {
      const groupKey = getItemGroup(item);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    }

    // Sort group keys
    const sortedGroups: Record<string, T[]> = {};
    Object.keys(groups)
      .sort()
      .forEach((key) => {
        sortedGroups[key] = groups[key];
      });

    return sortedGroups;
  }, [allItems, getItemGroup]);

  // Handle scroll to load more
  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const scrollPercentage =
        (target.scrollTop + target.clientHeight) / target.scrollHeight;

      // Load more when scrolled to 80%
      if (scrollPercentage > 0.8 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const handleSelect = (item: T) => {
    onSelect(item);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-8 justify-between border-[#2E3835] bg-[#1a1a1a] text-sm hover:bg-[#222]',
            className
          )}
          disabled={disabled}
        >
          {isLoading && !allItems.length ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <span className="flex-1 truncate text-left">
                {selectedItem ? getItemLabel(selectedItem) : placeholder}
              </span>
              <div className="flex items-center gap-1">
                {value && !disabled && (
                  <div
                    className="rounded-sm p-0.5 hover:bg-white/10"
                    onMouseDown={handleClear}
                  >
                    <X className="h-3 w-3 opacity-70" />
                  </div>
                )}
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className={cn('p-0', className)}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchTerm}
            onValueChange={setSearchTerm}
            className="h-9"
          />
          <CommandList ref={listRef} onScroll={handleScroll}>
            {isLoading && !allItems.length ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : allItems.length === 0 ? (
              <CommandEmpty>No items found.</CommandEmpty>
            ) : (
              <>
                {Object.entries(groupedItems).map(([groupKey, items]) => (
                  <CommandGroup
                    key={groupKey}
                    heading={getItemGroup ? formatGroupLabel(groupKey) : undefined}
                  >
                    {items.map((item) => {
                      const itemId = getItemId(item);
                      const itemLabel = getItemLabel(item);
                      return (
                        <CommandItem
                          key={itemId}
                          value={itemId}
                          keywords={[itemLabel]}
                          onSelect={() => handleSelect(item)}
                        >
                          <span className="flex-1 truncate">{itemLabel}</span>
                          <Check
                            className={cn(
                              'ml-2 h-4 w-4 shrink-0',
                              value === itemId ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
